import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface WorkersListGateContextValue {
  requestList: () => void;
  releaseList: () => void;
  listRequested: boolean;
}

const WorkersListGateContext = createContext<WorkersListGateContextValue | null>(
  null,
);

/** Tracks how many surfaces need the full workers list (directory, chat layout, search). */
export function WorkersListGateProvider({ children }: { children: ReactNode }) {
  const [requestCount, setRequestCount] = useState(0);

  const requestList = useCallback(() => {
    setRequestCount((c) => c + 1);
  }, []);

  const releaseList = useCallback(() => {
    setRequestCount((c) => Math.max(0, c - 1));
  }, []);

  const value = useMemo(
    () => ({
      requestList,
      releaseList,
      listRequested: requestCount > 0,
    }),
    [requestCount, requestList, releaseList],
  );

  return (
    <WorkersListGateContext.Provider value={value}>
      {children}
    </WorkersListGateContext.Provider>
  );
}

/** Mount in Workers surfaces that need the full list cached. */
export function useRequestWorkersList(): void {
  const ctx = useContext(WorkersListGateContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.requestList();
    return () => ctx.releaseList();
  }, [ctx]);
}

export function useWorkersListRequested(): boolean {
  return useContext(WorkersListGateContext)?.listRequested ?? false;
}

/** Global search dialog can opt in without mounting Workers UI. */
export function useWorkersListGate() {
  const ctx = useContext(WorkersListGateContext);
  if (!ctx) {
    throw new Error("useWorkersListGate must be used within WorkersListGateProvider");
  }
  return ctx;
}
